// src/pages/usePumpageReportForm.js
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useApp } from "../../AppProvider";

/* ------------------------------------------------------------------
   API Helper Functions
   ------------------------------------------------------------------ */
async function fetchMyPermits(token) {
  const { data } = await axios.get(
    `${process.env.REACT_APP_ENDPOINT}/api/pumpage-entry/my-permits`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
}

async function fetchPermittedEntities(token, permitIds) {
  const csv = permitIds.join(",");
  const { data } = await axios.get(
    `${process.env.REACT_APP_ENDPOINT}/api/pumpage-entry/ui-permitted-users-list`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { permit_ids: csv },
    }
  );
  return data;
}

async function fetchPumpageEntries(token, permit_id, year) {
  const { data } = await axios.get(
    `${process.env.REACT_APP_ENDPOINT}/api/pumpage-entry/pumpage-entry-calendar`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { permit_id, year },
    }
  );
  return data;
}

async function saveSinglePumpageEntry(token, monthEntry) {
  const { data } = await axios.put(
    `${process.env.REACT_APP_ENDPOINT}/api/pumpage-entry/single-month`,
    { monthEntry },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
}

/* ------------------------------------------------------------------
   Main Hook
   ------------------------------------------------------------------ */
export function usePumpageReportForm(selectedPermitHolder, selectedYear) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { doToast } = useApp();

  // 1.a) Grab my permit IDs
  const myPermitsQuery = useQuery(["my-permits"], async () => {
    const token = await getAccessTokenSilently();
    return fetchMyPermits(token);
  });

  // 2) From those permit IDs, fetch the permitted-entity rows
  const permittedEntitiesQuery = useQuery(
    ["permitted-entities", myPermitsQuery.data],
    async () => {
      if (!myPermitsQuery.data || myPermitsQuery.data.length === 0) return [];
      const token = await getAccessTokenSilently();
      return fetchPermittedEntities(token, myPermitsQuery.data);
    },
    {
      enabled: !!myPermitsQuery.data && myPermitsQuery.data.length > 0,
    }
  );

  // 3) For the chosen permit entity and year, fetch pumpage entries
  const pumpageEntriesQuery = useQuery(
    ["pumpage-entries", selectedPermitHolder, selectedYear],
    async () => {
      const token = await getAccessTokenSilently();
      if (!selectedPermitHolder || !selectedYear) {
        return [];
      }
      return fetchPumpageEntries(token, selectedPermitHolder, selectedYear);
    },
    {
      enabled: !!selectedPermitHolder && !!selectedYear,
    }
  );

  // 5) Mutation to save single month entry
  const saveSingleMonthMutation = useMutation(
    async (monthEntry) => {
      const token = await getAccessTokenSilently();
      return saveSinglePumpageEntry(token, monthEntry);
    },
    {
      onSuccess: (data) => {
        doToast("success", data?.message || "Pumpage data saved!");
        void queryClient.invalidateQueries([
          "pumpage-entries",
          selectedPermitHolder,
          selectedYear,
        ]);
      },
      onError: (error) => {
        console.error("Error saving pumpage entry:", error);
        doToast(
          "error",
          error?.response?.data?.error || "Failed to save pumpage entry."
        );
      },
    }
  );

  return {
    myPermitsQuery,
    permittedEntitiesQuery,
    pumpageEntriesQuery,
    saveSingleMonthMutation,
  };
}
